import React, { useState, useEffect } from 'react';
import Navbar from '../components/NavBar';
import ClubCard from '../components/ClubCard';
import { getAllClubs, joinClub, leaveClub, getMyClubs } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Target, Heart, MapPin, Navigation, X, ChevronDown, LogIn, Users, ArrowLeft, FolderPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DotLoader } from 'react-spinners';
import { requestNotificationPermission } from '../services/firebase';
import Footer from '../components/Footer';
import FooterPWA from '../components/FooterPWA';
import PulseLogo from '../assets/PulseLogoMin.png'

function ClubsListPage() {
  const { user, isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [myClubs, setMyClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('public');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [showLocationBanner, setShowLocationBanner] = useState(true);
  const navigate = useNavigate();

  // Charger la localisation depuis le localStorage au montage du composant
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setUserLocation(location);
        console.log('📍 Localisation récupérée du cache:', location);
      } catch (error) {
        console.error('Erreur lors du chargement de la localisation:', error);
        localStorage.removeItem('userLocation');
      }
    }
    setLocationLoaded(true);
  }, []);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Charger les clubs SEULEMENT après que la localisation soit chargée
  useEffect(() => {
    if (locationLoaded) {
      fetchClubs();
      if (isAuthenticated) {
        fetchMyClubs();
      }
    }
  }, [isAuthenticated, userLocation, locationLoaded]);

  const requestUserLocation = async () => {
    if (!("geolocation" in navigator)) {
      alert("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    setLocationLoading(true);

    const getPosition = () => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
    };

    try {
      const position = await getPosition();
      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now(),
      };
      setUserLocation(location);
      localStorage.setItem("userLocation", JSON.stringify(location));
      setShowLocationBanner(true);
      console.log("📍 Position utilisateur sauvegardée:", location);
    } catch (error) {
      console.error("❌ Erreur de géolocalisation:", error);
      if (error.code === 1) {
        alert("Veuillez autoriser la géolocalisation dans les réglages du navigateur.");
      } else if (error.code === 2) {
        alert("Position indisponible. Vérifiez votre connexion GPS ou Wi-Fi.");
      } else if (error.code === 3) {
        alert("Délai dépassé. Réessayez.");
      } else {
        alert("Erreur inconnue de géolocalisation.");
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const clearUserLocation = () => {
    setUserLocation(null);
    localStorage.removeItem('userLocation');
    setShowLocationBanner(false);
    console.log('🗑️ Localisation supprimée');
  };

  const fetchClubs = async () => {
    try {
      console.log('🔄 Chargement des clubs avec localisation:', userLocation);
      const data = await getAllClubs(userLocation);
      setClubs(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  const fetchMyClubs = async () => {
    try {
      const data = await getMyClubs();
      setMyClubs(data);
    } catch (error) {
      console.error('Erreur lors du chargement de mes clubs:', error);
    }
  };

  const handleJoinClub = async (clubId) => {
    if (!isAuthenticated) {
      alert('Vous devez être connecté pour rejoindre un club');
      return;
    }

    try {
      await joinClub(clubId);
      fetchClubs();
      fetchMyClubs();
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  };

  const handleLeaveClub = async (clubId) => {
    try {
      await leaveClub(clubId);
      fetchClubs();
      fetchMyClubs();
    } catch (error) {
      console.error('Erreur lors de la sortie du club:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sortie');
    }
  };

  const isMemberOfClub = (club) => {
    if (!isAuthenticated || !user) return false;
    return club.membres?.some(membre => membre === user._id || membre._id === user._id);
  };

  const getLocationAge = () => {
    if (!userLocation || !userLocation.timestamp) return null;
    const ageInHours = (Date.now() - userLocation.timestamp) / (1000 * 60 * 60);
    return Math.round(ageInHours * 10) / 10;
  };

  const locationAge = getLocationAge();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  // Filtrer les clubs en fonction de l'onglet actif
  const displayedClubs = (() => {
    if (activeTab === 'myClubs') return myClubs;
    if (activeTab === 'private') return clubs.filter(club => club.isPrivate);
    return clubs.filter(club => !club.isPrivate); // 'public' par défaut
  })();

  const publicClubs = clubs.filter(club => !club.isPrivate);
  const privateClubs = clubs.filter(club => club.isPrivate);

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <button
    onClick={() => navigate('/')}
    className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 bg-slate-900 border border-slate-800 hover:border-indigo-500 px-3 py-2 rounded-lg transition"
  >
    <ArrowLeft size={20} className="mr-2" />
    <span>Retour</span>
  </button>
        {/* Hero Section */}
        <div className="mb-10">
          <div className="text-center max-w-4xl mx-auto ">

            <p className="title-animated text-2xl sm:text-3xl md:text-4xl px-4 mt-10">
              Groupes de course
            </p>

            
            {/* CTA */}
            <div className="pt-1">
              {!isAuthenticated ? (
                // Non connecté
                <div className="space-y-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center space-x-2 px-8 sm:px-10 py-4 sm:py-5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-2xl font-medium transition-all text-base sm:text-lg shadow-xl shadow-indigo-500/20 hover:shadow-2xl hover:shadow-indigo-500/30 transform hover:scale-[1.02]"
                  >
                    <span>Se connecter</span>
                    <LogIn size={20} />
                  </button>
                  <p className="text-sm text-gray-400">
                    Pas encore inscrit ?{' '}
                    <button
                      onClick={() => navigate('/register')}
                      className="text-indigo-500 hover:text-indigo-400 font-semibold underline"
                    >
                      Créer un compte
                    </button>
                  </p>
                </div>
              ) : !userLocation ? (
                // Connecté sans localisation - Version subtile
                <button
                  onClick={requestUserLocation}
                  disabled={locationLoading}
                  className="inline-flex items-center space-x-2 px-6 py-2.5 bg-slate-800/50 hover:bg-slate-700 text-gray-300 hover:text-white rounded-lg font-medium transition-all disabled:opacity-50 text-sm border border-slate-700 hover:border-indigo-500"
                >
                  {locationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Localisation...</span>
                    </>
                  ) : (
                    <>
                      <Navigation size={16} />
                      <span>Activer la localisation</span>
                    </>
                  )}
                </button>
              ) : null}
              {/* Si localisation active, on n'affiche rien */}
            </div>

            {/* Stats minimalistes */}
            {userLocation && (
              <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-sm text-gray-400 px-4">
                {locationAge !== null && locationAge > 1 && (
                  <>
                    <button
                      onClick={requestUserLocation}
                      className="hover:text-gray-300 underline"
                    >
                      Actualiser ma position
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs de navigation */}
        <div className="mb-4 sm:mb-6">
          {/* Tabs */}
          <div className="flex gap-2 sm:gap-4 border-b border-slate-800 overflow-x-auto pb-0 scrollbar-hide">
            <button
              onClick={() => setActiveTab('public')}
              className={`pb-2 sm:pb-3 px-3 sm:px-4 font-semibold transition flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                activeTab === 'public'
                  ? 'text-indigo-500 border-b-2 border-indigo-500'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <Target size={18} className="sm:w-5 sm:h-5" />
              <span>Clubs Publics ({publicClubs.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('private')}
              className={`pb-2 sm:pb-3 px-3 sm:px-4 font-semibold transition flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                activeTab === 'private'
                  ? 'text-indigo-500 border-b-2 border-indigo-500'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <MapPin size={18} className="sm:w-5 sm:h-5" />
              <span>Clubs Privés ({privateClubs.length})</span>
            </button>

            {isAuthenticated && (
              <button
                onClick={() => setActiveTab('myClubs')}
                className={`pb-2 sm:pb-3 px-3 sm:px-4 font-semibold transition flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                  activeTab === 'myClubs'
                    ? 'text-indigo-500 border-b-2 border-indigo-500'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Heart size={18} className="sm:w-5 sm:h-5" />
                <span>Mes Clubs ({myClubs.length})</span>
              </button>
            )}
          </div>

          {/* Boutons pour la section publique */}
          {activeTab === 'public' && isAuthenticated && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 sm:mt-4">
              {/* Carte Clubs recommandés */}
              <div
                onClick={() => navigate('/recommended-clubs')}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 rounded-xl shadow-md p-3 sm:p-4 cursor-pointer transition transform hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Target size={20} className="text-yellow-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-white">
                      Clubs recommandés
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Trouvez les clubs parfaits pour vous
                    </p>
                  </div>
                  <ChevronDown size={18} className="text-yellow-500 transform -rotate-90 flex-shrink-0" />
                </div>
              </div>

              {/* Carte interactive */}
              <div
                onClick={() => navigate('/map')}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 rounded-xl shadow-md p-3 sm:p-4 cursor-pointer transition transform hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <MapPin size={20} className="text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-white">
                      Carte interactive
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Visualisez les clubs près de vous
                    </p>
                  </div>
                  <ChevronDown size={18} className="text-green-500 transform -rotate-90 flex-shrink-0" />
                </div>
              </div>
            </div>
          )}

          {/* Cartes d'actions rapides */}
          {isAuthenticated && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 sm:mt-4">
              {/* Carte "Clubs de mes amis" */}
              <div
                onClick={() => navigate('/following-clubs')}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 rounded-xl shadow-md p-3 sm:p-4 cursor-pointer transition transform hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                    <Users size={20} className="text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-white">
                      Clubs de mes amis
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Découvrez où vos amis courent
                    </p>
                  </div>
                  <ChevronDown size={18} className="text-indigo-500 transform -rotate-90 flex-shrink-0" />
                </div>
              </div>

              {/* Carte "Mes créations" */}
              <div
                onClick={() => navigate('/my-created-clubs')}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 rounded-xl shadow-md p-3 sm:p-4 cursor-pointer transition transform hover:scale-[1.01] active:scale-[0.99]"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <FolderPlus size={20} className="text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-white">
                      Mes créations
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Gérez vos clubs créés
                    </p>
                  </div>
                  <ChevronDown size={18} className="text-purple-500 transform -rotate-90 flex-shrink-0" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3 sm:space-y-6">
          {displayedClubs.map((club, index) => (
            <div
              key={club._id}
              className="club-appear"
              style={{ animationDelay: `${index * 0.2}s` }} // 0s, 0.2s, 0.4s...
            >
              <ClubCard
                club={club}
                onJoin={handleJoinClub}
                onLeave={handleLeaveClub}
                isMember={isMemberOfClub(club)}
                userLocation={userLocation}
              />
            </div>
          ))}
        </div>

        {/* Message vide */}
        {displayedClubs.length === 0 && (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="text-6xl sm:text-7xl mb-4">🏃‍♂️</div>
            <p className="text-lg sm:text-xl text-white mb-2">
              {activeTab === 'myClubs' 
                ? 'Vous n\'avez rejoint aucun club'
                : 'Aucun club disponible'}
            </p>
            <p className="text-sm sm:text-base text-gray-400">
              {activeTab === 'myClubs' 
                ? 'Parcourez les clubs disponibles et rejoignez-en un !'
                : 'Revenez plus tard ou créez votre propre club.'}
            </p>
          </div>
        )}

        {/* Bouton flottant créer un club */}
        {isAuthenticated && (
          <button
            onClick={() => navigate('/create')}
            className="md:hidden fixed bottom-6 right-6 bg-indigo-500 hover:bg-indigo-600 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all transform hover:scale-110 active:scale-95 z-40"
            aria-label="Créer un club"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 4v16m8-8H4" 
              />
            </svg>
          </button>
        )}
      </main>
      <Footer />
      <FooterPWA />
    </div>
  );
}

export default ClubsListPage;